import { Component, ChangeDetectionStrategy, signal, computed, inject, effect, viewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
// FIX: Import the `Document` interface to use it as a type annotation, resolving ambiguity for the TypeScript compiler.
import { OnboardingData, FleetContactDetails, FleetOperations, Confirmations, DocumentType, Document, Address, SignedVersions } from './models/onboarding-data.model';
import { SupabaseService, LegalDocRecord } from './supabase.service';
import { ServiceManagerService } from './service-manager.service';
import { initialOnboardingData } from './models/initial-onboarding-data';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

// FIX: Declare the global `google` object to inform TypeScript that it will be available at runtime, likely from a script tag in index.html. This resolves the "Cannot find name 'google'" errors.
declare const google: any;

type ConfirmationDocType = 'terms' | 'dataSharing' | 'codeOfConduct';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProgressBarComponent, CommonModule]
})
export class AppComponent {
  supabaseService = inject(SupabaseService);
  serviceManagerService = inject(ServiceManagerService);
  googleMapsLoaderService = inject(GoogleMapsLoaderService);
  
  logoUrl = 'assets/SuperYes-Logo-Horizontal-Positive@2x.png';
  currentStep = signal(1);
  data = signal<OnboardingData>(initialOnboardingData);
  
  addressInput = viewChild<ElementRef<HTMLInputElement>>('addressInput');
  tokenInput = viewChild<ElementRef<HTMLInputElement>>('tokenInputRef');
  // FIX: Corrected a typo from `viewchild` to `viewChild` to properly query for the modal content element in the component's template. This resolves the "Cannot find name 'viewchild'" compilation error.
  modalContent = viewChild<ElementRef<HTMLElement>>('modalContent');
  sliderTrack = viewChild<ElementRef<HTMLDivElement>>('sliderTrack');
  
  isOtpRequested = signal(false);
  authStatus = signal<'idle' | 'submitting' | 'success'>('idle');
  authError = signal<string | null>(null);
  initError = signal<string | null>(null);
  token = signal('');
  addressInputText = signal('');
  wasRejected = signal(false);
  
  steps = [ 'Email', 'Fleet Details', 'Operations', 'Documents', 'Confirmations', 'Complete' ];
  progressBarSteps = [ 'Fleet Details', 'Operations', 'Documents', 'Confirmations' ];
  stepKeys = ['', 'email', 'contact_details', 'fleet_operations', 'documents', 'confirmations', 'complete'];
  stepKeyToNumber: { [key: string]: number } = {
    'contact_details': 2,
    'fleet_operations': 3,
    'documents': 4,
    'confirmations': 5,
    'complete': 6,
  };

  isMobileDevice = signal(typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  isSubmitting = signal(false);
  isUploadingDoc = signal<DocumentType | null>(null);
  uploadError = signal<string | null>(null);
  viewingDocumentUrl = signal<string | null>(null);

  confirmationModal = signal<{type: ConfirmationDocType, title: string, content: string, canAgree: boolean} | null>(null);
  legalDocs = signal<Map<string, LegalDocRecord>>(new Map());

  // --- OTP Resend Logic ---
  resendCooldown = signal(0);
  private resendTimer: any = null;

  // --- Payment Split Slider Logic ---
  activeHandle = signal<'account' | 'card' | null>(null);
  private boundOnDrag: (event: MouseEvent | TouchEvent) => void;
  private boundStopDrag: () => void;
  
  documentKeys: DocumentType[] = ['EmployersLiabilityInsurance', 'PublicLiabilityInsurance', 'OperatorsLicence'];
  documentTitles: { [key in DocumentType]: string } = {
    'EmployersLiabilityInsurance': 'Employers Liability Insurance',
    'PublicLiabilityInsurance': 'Public Liability Insurance',
    'OperatorsLicence': 'Operator\'s Licence'
  };

  constructor() {
    this.googleMapsLoaderService.load().catch(err => {
        console.error('Failed to load Google Maps script', err);
        this.initError.set('Could not load address lookup functionality. Please check your internet connection or API key configuration.');
    });
    this.boundOnDrag = this.onDrag.bind(this);
    this.boundStopDrag = this.stopDrag.bind(this);

    // Fetch active legal documents from the database
    this.supabaseService.getActiveLegalDocuments().then(docs => {
      const map = new Map<string, LegalDocRecord>();
      for (const doc of docs) map.set(doc.slug, doc);
      this.legalDocs.set(map);
    }).catch(err => {
      console.error('Failed to load legal documents:', err);
    });

    // Effect to watch for critical initialization errors from the service
    effect(() => {
      this.initError.set(this.supabaseService.initError());
    });
      
    // Effect to react to changes from the Supabase service
    effect(() => {
      const supabaseData = this.supabaseService.data();
      if (supabaseData) {
        this.data.set(supabaseData);
        if (supabaseData.status === 'rejected') {
            this.wasRejected.set(true);
            // If the user's application was rejected and they are on the 'complete' step,
            // move them back to the first editable step so they can make corrections.
            if (supabaseData.currentStepKey === 'complete') {
                this.currentStep.set(2); 
                return; // Stop further processing for this case
            }
        }
        const stepNumber = this.stepKeyToNumber[supabaseData.currentStepKey] || 2;
        // If user is logged in and has data, they can skip the email step
        if (this.currentStep() === 1 && this.supabaseService.session()) {
            this.currentStep.set(stepNumber);
        }
      } else {
        // If data is null (e.g., after sign-out), reset to the initial state,
        // but only if we are not on the completion page. This allows the
        // completion message to be displayed even after the session is terminated.
        if (this.currentStep() !== 6) {
            // Reset to step 1 if user logs out or has no data
            this.isOtpRequested.set(false);
            this.data.set(initialOnboardingData);
            this.currentStep.set(1);
            this.wasRejected.set(false);
        }
      }
    });

    // Auto-save form data to Supabase after the user pauses typing (2s debounce).
    // This ensures in-progress data survives browser tab discarding or session refresh.
    let autoSaveTimer: any = null;
    effect(() => {
      const currentData = this.data();
      const session = this.supabaseService.session();
      // Only auto-save if authenticated and have an applicationId, and not on step 1 (email) or 6 (complete)
      if (!session || !currentData.applicationId || this.currentStep() === 1 || this.currentStep() === 6) {
        return;
      }
      clearTimeout(autoSaveTimer);
      autoSaveTimer = setTimeout(() => {
        this.supabaseService.saveOnboardingData(currentData);
      }, 2000);
    });

    // Effect to sync the address input field when data is loaded from the database
    effect(() => {
        const address = this.data().contactDetails.address;
        if (address.line1 && address.postcode) {
             this.addressInputText.set([address.line1, address.line2, address.city, address.postcode].filter(Boolean).join(', '));
        }
    }, { allowSignalWrites: true });


    // Effect to initialize Google Places Autocomplete using the classic JS library
    effect((onCleanup) => {
      const inputElement = this.addressInput()?.nativeElement;

      // Only initialize if we are on step 2, the input is rendered, and the google object is available
      if (this.currentStep() === 2 && inputElement && this.googleMapsLoaderService.isLoaded()) {
        
        const autocomplete = new google.maps.places.Autocomplete(inputElement, {
            types: ['address'],
            componentRestrictions: { country: 'gb' }, // Strictly restrict to UK
            fields: ['address_components', 'geometry.location', 'formatted_address']
        });

        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place?.address_components && place?.geometry?.location) {
                const newAddress = this.parseAddressComponents(place);
                this.data.update(d => ({ ...d, contactDetails: { ...d.contactDetails, address: newAddress } }));
                this.addressInputText.set(place.formatted_address || '');
            }
        });
        
        onCleanup(() => {
            google.maps.event.clearInstanceListeners(autocomplete);
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(c => c.remove());
        });
      }
    });


    // Effect to auto-focus the token input when it appears
    effect(() => {
      if (this.isOtpRequested() && this.tokenInput()) {
        // Use a microtask to ensure the element is rendered before focus
        Promise.resolve().then(() => this.tokenInput()!.nativeElement.focus());
      }
    });

    // Effect to manage the 'I Agree' button state in the confirmation modal.
    // It enables the button if the content is not scrollable, or if the user
    // scrolls to the bottom. It also uses a ResizeObserver to handle
    // viewport changes (like device rotation) robustly.
    effect((onCleanup) => {
      const modalEl = this.modalContent()?.nativeElement;
      
      if (modalEl) {
        // A ResizeObserver is used to re-evaluate the scroll state whenever the
        // size of the modal content area changes.
        const observer = new ResizeObserver(() => this.checkModalScrollState(modalEl));
        observer.observe(modalEl);

        // Perform an initial check when the modal is first rendered.
        this.checkModalScrollState(modalEl);
        
        onCleanup(() => {
          observer.disconnect();
        });
      }
    });
    
    // Effect to clean up resend timer
    effect((onCleanup) => {
        onCleanup(() => {
            if (this.resendTimer) clearInterval(this.resendTimer);
        });
    });

    // Effect to sign out user on completion step
    effect(() => {
        if (this.currentStep() === 6) {
            // We sign out here so if the user refreshes, they start from scratch.
            // The data in the component is preserved for the completion screen view.
            this.supabaseService.signOut();
        }
    });

    // Effect to sync legal name to fleet name when checkbox is ticked
    effect(() => {
      if (this.data().contactDetails.useLegalNameAsFleetName) {
        const legalName = this.data().contactDetails.legalEntityName;
        const fleetName = this.data().contactDetails.fleetName;

        // FIX: Add a condition to prevent an infinite loop. Only update if the names differ.
        if (legalName !== fleetName) {
          this.data.update(d => ({
            ...d,
            contactDetails: {
              ...d.contactDetails,
              fleetName: legalName,
            }
          }));
        }
      }
    }, { allowSignalWrites: true });
  }

  // FIX: Changed the type of the `place` parameter to `any` to resolve the "Cannot find namespace 'google'" error, as the `google` object is declared as `any` without full type definitions.
  private parseAddressComponents(place: any): Address {
    const address: Address = {
        line1: '', line2: '', city: '', county: '', postcode: '',
        latitude: place.geometry?.location?.lat() ?? null,
        longitude: place.geometry?.location?.lng() ?? null,
    };

    const componentMap: { [key: string]: string } = {};
    place.address_components?.forEach((component: any) => {
        // Store the long name for each type present in the component
        component.types.forEach((type: string) => {
            componentMap[type] = component.long_name;
        });
    });

    const streetNumber = componentMap['street_number'] || '';
    const route = componentMap['route'] || '';
    address.line1 = `${streetNumber} ${route}`.trim();
    
    // Sometimes the street address is in 'premise' or 'subpremise'
    if (!address.line1 && (componentMap['premise'] || componentMap['subpremise'])) {
        address.line1 = `${componentMap['premise'] || ''} ${componentMap['subpremise'] || ''}`.trim();
    }
    
    address.city = componentMap['postal_town'] || componentMap['locality'] || '';
    address.county = componentMap['administrative_area_level_2'] || '';
    address.postcode = componentMap['postal_code'] || '';

    return address;
  }

  isGuid(filename: string): boolean {
    if (!filename) return false;
    const nameOnly = filename.split('.')[0];
    // crypto.randomUUID() format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(nameOnly);
  }

  async requestOtp() {
    if (!this.isStep1Valid()) return;
    this.authStatus.set('submitting');
    this.authError.set(null);
    this.token.set(''); // Clear token on new request
    const { error } = await this.supabaseService.signInWithEmailOtp(this.data().email);
    if (error) {
      this.authError.set(error.message);
    } else {
      this.isOtpRequested.set(true);
      this.startResendCooldown();
    }
    this.authStatus.set('idle');
  }
  
  resendOtp() {
    if (this.resendCooldown() > 0) return;
    this.requestOtp();
  }

  startResendCooldown() {
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendCooldown.set(30);
      this.resendTimer = setInterval(() => {
          this.resendCooldown.update(c => c - 1);
          if (this.resendCooldown() <= 0) {
              clearInterval(this.resendTimer);
          }
      }, 1000);
  }

  async verifyToken() {
    this.authStatus.set('submitting');
    this.authError.set(null);

    if (!this.isTokenValid()) {
      this.authStatus.set('idle');
      return;
    }
    
    const { error } = await this.supabaseService.verifyToken(this.data().email, this.token());
    if (error) {
      this.authError.set(error.message);
      this.authStatus.set('idle');
    } else {
      this.authStatus.set('success');
      // onAuthStateChange will trigger navigation
    }
  }
  
  // --- Step Validation ---
  isStep1Valid = computed(() => this.data().email.includes('@') && this.data().email.includes('.'));
  isTokenValid = computed(() => this.token().length === 6 && /^\d+$/.test(this.token()));
  
  isLegalEntityNameValid = computed(() => this.data().contactDetails.legalEntityName.length > 2);
  isFleetNameValid = computed(() => this.data().contactDetails.fleetName.length > 2);
  isFleetPhoneValid = computed(() => { const v = this.data().contactDetails.fleetPhone; return v.length > 6 && /^\d+$/.test(v); });
  isAddressValid = computed(() => {
    const addr = this.data().contactDetails.address;
    return addr.line1.length > 3 && addr.postcode.length > 4;
  });
  isContactNameValid = computed(() => this.data().contactDetails.contactName.length > 2);
  isContactPhoneValid = computed(() => { const v = this.data().contactDetails.contactPhone; return v.length > 6 && /^\d+$/.test(v); });
  isContactEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.data().contactDetails.contactEmail));
  isStep2Valid = computed(() => this.isLegalEntityNameValid() && this.isFleetNameValid() && this.isFleetPhoneValid() && this.isAddressValid() && this.isContactNameValid() && this.isContactPhoneValid() && this.isContactEmailValid());

  isPaymentSplitValid = computed(() => {
    const { account, card, cash } = this.data().fleetOps.workSplit.paymentType;
    return account + card + cash === 100;
  });
  isStep3Valid = computed(() => {
    const ops = this.data().fleetOps;
    const isDispatchValid = ops.dispatchSystem !== 'Other' || (ops.dispatchSystem === 'Other' && ops.dispatchSystemOther.trim().length > 2);
    return ops.fleetSize !== '' && ops.serviceLevel !== '' && ops.dispatchSystem !== '' && isDispatchValid && this.isPaymentSplitValid();
  });

  docValidationErrors = computed(() => {
    const errors: { [key in DocumentType]?: string } = {};
    const docs = this.data().documents;

    for (const key in docs) {
      const docType = key as DocumentType;
      const doc = docs[docType];
      if (!doc || !doc.url) {
        errors[docType] = 'A document file is required.';
      }
    }
    return errors;
  });
  isStep4Valid = computed(() => Object.keys(this.docValidationErrors()).length === 0);
  
  isStep5Valid = computed(() => {
    const { terms, dataSharing, codeOfConduct } = this.data().confirmations;
    return terms && dataSharing && codeOfConduct;
  });

  // --- Keyboard Handlers ---
  handleStep2SubmitOnEnter(event: KeyboardEvent | Event) {
    if (document.querySelector('.pac-container')) { return; }
    event.preventDefault();
    if (this.isStep2Valid()) { this.saveAndGo('next'); }
  }

  // --- Methods ---
  async saveAndGo(direction: 'next' | 'prev') {
    const currentStep = this.currentStep();
    let nextStep = currentStep;
    if (direction === 'next' && currentStep < this.steps.length) { nextStep = currentStep + 1; } 
    else if (direction === 'prev' && currentStep > 1) { nextStep = currentStep - 1; }
    
    // No change in step, do nothing.
    if (nextStep === currentStep) return;

    const currentStepKey = this.stepKeys[currentStep];
    const nextStepKey = this.stepKeys[nextStep];
    
    const success = await this.supabaseService.saveOnboardingData(this.data(), nextStepKey);
    
    if (success) {
      if (this.data().applicationId) {
        await this.supabaseService.logStepProgression(this.data().applicationId!, currentStepKey, nextStepKey);
      }
      this.currentStep.set(nextStep);
      window.scrollTo(0, 0);
    }
  }

  goToStep(step: number) {
    if (step > 0 && step <= this.steps.length) { this.currentStep.set(step); }
  }

  updateData<K1 extends keyof OnboardingData, K2 extends keyof OnboardingData[K1]>(key1: K1, key2: K2, value: OnboardingData[K1][K2]) {
    // FIX: The original implementation had a type error because `d[key1]` could be a primitive type (like a string), which cannot be spread. This is corrected by adding a type guard to ensure the property is an object before attempting the update.
    this.data.update(d => {
      const prop = d[key1];
      if (typeof prop !== 'object' || prop === null) {
        // If the property is not a non-null object, return the state unchanged.
        // This function is designed to update nested properties and should not be used on primitives.
        return d;
      }
      return { ...d, [key1]: { ...prop, [key2]: value } };
    });
  }

  async handleFileInput(event: Event, docType: DocumentType) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;
    
    const file = input.files[0];
    const applicationId = this.data().applicationId;
    if (!applicationId) { this.authError.set('Could not upload file. Application ID not found.'); return; }

    this.isUploadingDoc.set(docType);
    this.uploadError.set(null);
    const { document: newDocument, error } = await this.supabaseService.uploadDocument(applicationId, file, docType, this.data().documents[docType]?.id);
    
    if (error) { this.uploadError.set(error); } 
    else if (newDocument) { this.data.update(d => ({...d, documents: {...d.documents, [docType]: newDocument }})); }
    
    this.isUploadingDoc.set(null);
    input.value = '';
  }

  updateEmail(event: Event) {
    const email = (event.target as HTMLInputElement).value;
    this.data.update(d => ({...d, email, contactDetails: {...d.contactDetails, contactEmail: email}}));
  }
  
  updateAdditionalNotes(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    this.data.update(d => ({...d, additionalNotes: value}));
  }

  updateContactDetails(field: keyof Omit<FleetContactDetails, 'address' | 'useLegalNameAsFleetName'>, event: Event) {
    const input = event.target as HTMLInputElement;
    const isPhoneField = field === 'fleetPhone' || field === 'contactPhone';
    const value = isPhoneField ? input.value.replace(/\D/g, '') : input.value;
    if (isPhoneField) input.value = value;
    this.data.update(d => ({...d, contactDetails: {...d.contactDetails, [field]: value}}));
  }
  
  toggleUseLegalNameAsFleetName() {
    this.data.update(d => ({
      ...d,
      contactDetails: {
        ...d.contactDetails,
        useLegalNameAsFleetName: !d.contactDetails.useLegalNameAsFleetName
      }
    }));
  }

  updateFleetOps(field: keyof Omit<FleetOperations, 'workSplit'>, event: Event) {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;

    if (field === 'dispatchSystem') {
      this.data.update(d => ({
        ...d,
        fleetOps: {
          ...d.fleetOps,
          dispatchSystem: value as FleetOperations['dispatchSystem'],
          // Clear the 'Other' text if a pre-defined system is chosen
          dispatchSystemOther: value === 'Other' ? d.fleetOps.dispatchSystemOther : ''
        }
      }));
    } else {
      this.data.update(d => ({ ...d, fleetOps: { ...d.fleetOps, [field]: value } }));
    }
  }
  
  updateBookingSplit(event: Event) {
    const prebook = parseInt((event.target as HTMLInputElement).value, 10);
    this.data.update(d => ({...d, fleetOps: {...d.fleetOps, workSplit: {...d.fleetOps.workSplit, bookingType: {prebook, asap: 100 - prebook}}}}));
  }

  // --- Payment Split Slider ---
  startDrag(event: MouseEvent | TouchEvent, handle: 'account' | 'card') {
    if (this.activeHandle()) return;
    event.preventDefault();
    this.activeHandle.set(handle);
    document.addEventListener('mousemove', this.boundOnDrag, { passive: false });
    document.addEventListener('mouseup', this.boundStopDrag);
    document.addEventListener('touchmove', this.boundOnDrag, { passive: false });
    document.addEventListener('touchend', this.boundStopDrag);
  }

  onDrag(event: MouseEvent | TouchEvent) {
    if (!this.activeHandle()) return;
    event.preventDefault();

    const track = this.sliderTrack()?.nativeElement;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    
    this.data.update(d => {
      const currentSplit = d.fleetOps.workSplit.paymentType;
      let p1 = currentSplit.account;
      let p2 = currentSplit.account + currentSplit.card;
      
      if (this.activeHandle() === 'account') {
        p1 = Math.min(percent, p2);
      } else { // 'card'
        p2 = Math.max(percent, p1);
      }
      
      const newAccount = Math.round(p1);
      const newCard = Math.round(p2 - p1);
      const newCash = 100 - newAccount - newCard;

      return { ...d, fleetOps: { ...d.fleetOps, workSplit: { ...d.fleetOps.workSplit, paymentType: { account: newAccount, card: newCard, cash: newCash } } } };
    });
  }

  stopDrag() {
    this.activeHandle.set(null);
    document.removeEventListener('mousemove', this.boundOnDrag);
    document.removeEventListener('mouseup', this.boundStopDrag);
    document.removeEventListener('touchmove', this.boundOnDrag);
    document.removeEventListener('touchend', this.boundStopDrag);
  }
  
  updateToken(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    this.token.set(value);
    if (value.length === 6 && /^\d+$/.test(value)) {
      this.authStatus.set('submitting');
      this.verifyToken();
    }
  }
  
  async submitApplication() {
    this.isSubmitting.set(true);
    // Final confirmation is implicit with the click
    this.data.update(d => ({...d, confirmations: {...d.confirmations, isAuthorised: true }}));
    await this.supabaseService.saveOnboardingData(this.data()); // Save final changes
    if (this.data().applicationId) {
      await this.supabaseService.submitApplication(this.data().applicationId!, this.wasRejected());
      this.serviceManagerService.notifyApplicationSubmitted(this.data().applicationId!, this.data()); // fire-and-forget
    }
    this.currentStep.set(this.steps.length);
    this.isSubmitting.set(false);
  }
  
  viewDocument(doc: Document | null) {
    if (doc?.url) {
      // Open PDFs in a new tab, as they can't be rendered in the image viewer
      if (doc.fileName?.toLowerCase().endsWith('.pdf')) {
        window.open(doc.url, '_blank');
      } else {
        this.viewingDocumentUrl.set(doc.url);
      }
    }
  }

  closeDocumentViewer() { this.viewingDocumentUrl.set(null); }

  openConfirmationModal(type: ConfirmationDocType) {
    const slugMap: Record<ConfirmationDocType, string> = {
      terms: 'terms_of_service',
      dataSharing: 'data_sharing_agreement',
      codeOfConduct: 'code_of_conduct',
    };

    const doc = this.legalDocs().get(slugMap[type]);
    if (!doc) {
      console.error(`Legal document not loaded for type: ${type}`);
      return;
    }

    let content = doc.html_content;

    if (type === 'dataSharing') {
      const currentData = this.data();
      const contactDetails = currentData.contactDetails;
      const address = contactDetails.address;

      const formattedAddress = [address.line1, address.line2, address.city, address.county, address.postcode].filter(Boolean).join(', ');
      const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

      content = content
        .replace(/\[date\]/g, today)
        .replace(/\[Party B full legal name\]/g, contactDetails.legalEntityName || '<i>Your Company Name</i>')
        .replace(/\[address\]/g, formattedAddress || '<i>Your Company Address</i>');
    }

    this.confirmationModal.set({ type, title: doc.title, content, canAgree: false });
  }

  private checkModalScrollState(element: HTMLElement) {
    // Use requestAnimationFrame to ensure we measure after the browser has painted,
    // which gives us the most accurate scroll dimensions.
    requestAnimationFrame(() => {
      // The modal could be closed between the event and the animation frame,
      // so we double-check that it's still active.
      if (this.confirmationModal()) {
        const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 5; // 5px tolerance

        if (isAtBottom) {
          this.confirmationModal.update(m => {
            // Only update if the state needs to change to avoid unnecessary signal writes.
            if (m && !m.canAgree) {
              return { ...m, canAgree: true };
            }
            return m;
          });
        }
      }
    });
  }

  onConfirmationScroll(event: Event) {
    this.checkModalScrollState(event.target as HTMLElement);
  }
  
  acceptConfirmation() {
    const modal = this.confirmationModal();
    if (modal) {
      const { type } = modal;
      const now = new Date().toISOString();

      const slugMap: Record<ConfirmationDocType, keyof SignedVersions> = {
        terms: 'terms_of_service',
        dataSharing: 'data_sharing_agreement',
        codeOfConduct: 'code_of_conduct',
      };
      const slug = slugMap[type];
      const doc = this.legalDocs().get(slug);

      this.data.update(d => {
        const newConfirmations = { ...d.confirmations };
        const currentVersions: SignedVersions = newConfirmations.signedVersions
          ? { ...newConfirmations.signedVersions }
          : { terms_of_service: 0, code_of_conduct: 0, data_sharing_agreement: 0 };

        if (type === 'terms') {
          newConfirmations.terms = true;
          newConfirmations.termsAcceptedAt = now;
        } else if (type === 'dataSharing') {
          newConfirmations.dataSharing = true;
          newConfirmations.dataSharingAcceptedAt = now;
        } else if (type === 'codeOfConduct') {
          newConfirmations.codeOfConduct = true;
          newConfirmations.codeOfConductAcceptedAt = now;
        }

        currentVersions[slug] = doc?.version ?? 1;
        newConfirmations.signedVersions = currentVersions;

        return {
          ...d,
          confirmations: newConfirmations
        };
      });
      this.confirmationModal.set(null);
    }
  }

  async startOver() {
    await this.supabaseService.signOut();
  }
}